import {
  useGetDipDcaStateQuery,
  useGetDipDcaRecentTicksQuery,
  useDipDcaTickNowMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { toast } from "sonner";

/**
 * DipDcaCard — per-asset dip-DCA state.
 *
 * Ticks every 30 min. Per coin: only buys when at fresh 7d-low.
 * After a fill, 24h cooldown UNLESS price dropped another 3% from
 * the last fill (dip-add ladder, sized 1x/1.5x/2x/...). Hard cap
 * $300 invested per coin.
 */
export function DipDcaCard() {
  const stateQ = useGetDipDcaStateQuery(undefined, { pollingInterval: 60000 });
  const ticksQ = useGetDipDcaRecentTicksQuery({ limit: 20 }, { pollingInterval: 30000 });
  const [tickNow, tickState] = useDipDcaTickNowMutation();

  const states: any[] = stateQ.data?.state || [];
  const ticks: any[] = ticksQ.data?.ticks || [];
  const lastTickTs = ticks[0]?.ts;
  const lastBuy = states.reduce<string | null>(
    (m, s: any) => (s.last_fill_at && (!m || s.last_fill_at > m) ? s.last_fill_at : m),
    null);

  async function onTickNow() {
    try {
      const r: any = await tickNow().unwrap();
      const buys = r?.buys_count ?? 0;
      toast.success(buys > 0
        ? `Dip-DCA tick: ${buys} buy(s) fired`
        : `Dip-DCA tick: no dips (${r?.symbols_scanned ?? 0} scanned)`);
      await stateQ.refetch(); await ticksQ.refetch();
    } catch (e: any) {
      toast.error(`tick failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span>Dip-DCA</span>
          <StalenessBadge ts={lastTickTs} warnHours={1} staleHours={2}
                          veryStaleHours={6} label="last tick" />
          {lastBuy && (
            <StalenessBadge ts={lastBuy} warnHours={48} staleHours={168}
                            veryStaleHours={336} label="last buy" />
          )}
          <span className="text-xs text-muted font-normal">
            per-coin · 30min tick · only buys at 7d-low · dip-add ladder
          </span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={onTickNow}
                disabled={tickState.isLoading}>
          {tickState.isLoading ? "Ticking…" : "Tick now"}
        </Button>
      </CardHeader>
      <CardContent>
        {stateQ.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="space-y-3 text-sm">
            {/* Active positions */}
            {states.length === 0 ? (
              <div className="text-sm text-muted italic">
                No dip buys yet. Engine waits for coins in the DCA universe
                to hit a fresh 7-day low.
              </div>
            ) : (
              <div className="overflow-x-auto border border-border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-bg2 text-left text-muted sticky top-0">
                    <tr>
                      <th className="py-1 px-2">Symbol</th>
                      <th className="py-1 px-2 text-right">Fills</th>
                      <th className="py-1 px-2 text-right">Invested</th>
                      <th className="py-1 px-2 text-right">Cap</th>
                      <th className="py-1 px-2 text-right">Avg entry</th>
                      <th className="py-1 px-2 text-right">Last fill px</th>
                      <th className="py-1 px-2 text-right">Streak</th>
                      <th className="py-1 px-2">Last fill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {states.map((s: any) => {
                      const invested = Number(s.total_invested_usd || 0);
                      const cap = 300;     // matches default settings
                      const pctOfCap = (invested / cap) * 100;
                      return (
                        <tr key={s.symbol} className="border-t border-border/40">
                          <td className="py-1 px-2 font-mono font-semibold">{s.symbol}</td>
                          <td className="py-1 px-2 text-right font-mono">{s.num_fills}</td>
                          <td className="py-1 px-2 text-right font-mono">
                            ${invested.toFixed(2)}
                          </td>
                          <td className="py-1 px-2 text-right">
                            <Badge tone={pctOfCap >= 90 ? "bad"
                                          : pctOfCap >= 60 ? "warn" : "muted"}>
                              {pctOfCap.toFixed(0)}%
                            </Badge>
                          </td>
                          <td className="py-1 px-2 text-right font-mono">
                            ${Number(s.avg_entry_price || 0).toFixed(4)}
                          </td>
                          <td className="py-1 px-2 text-right font-mono">
                            ${Number(s.last_fill_price || 0).toFixed(4)}
                          </td>
                          <td className="py-1 px-2 text-right">
                            <Badge tone={s.dip_streak_count >= 3 ? "bad"
                                          : s.dip_streak_count >= 2 ? "warn" : "muted"}>
                              {s.dip_streak_count}
                            </Badge>
                          </td>
                          <td className="py-1 px-2 text-muted">
                            {(s.last_fill_at || "").slice(5, 16).replace("T", " ")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent tick log */}
            <div>
              <div className="text-xs text-muted mb-1">
                Recent tick decisions (last {ticks.length})
              </div>
              {ticks.length === 0 ? (
                <div className="text-xs text-muted italic">
                  No ticks yet — wait 30 min or click "Tick now".
                </div>
              ) : (
                <div className="border border-border rounded overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-bg2 text-left text-muted sticky top-0">
                      <tr>
                        <th className="py-1 px-2">Time</th>
                        <th className="py-1 px-2">Symbol</th>
                        <th className="py-1 px-2">Action</th>
                        <th className="py-1 px-2 text-right">Price</th>
                        <th className="py-1 px-2 text-right">7d-low</th>
                        <th className="py-1 px-2">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticks.map((t: any) => (
                        <tr key={t.id} className="border-t border-border/40">
                          <td className="py-1 px-2 text-muted">
                            {(t.ts || "").slice(5, 19).replace("T", " ")}
                          </td>
                          <td className="py-1 px-2 font-mono">{t.symbol}</td>
                          <td className="py-1 px-2">
                            <Badge tone={t.action === "buy" ? "good"
                                          : t.action === "skip_not_dip" ? "muted"
                                          : t.action === "skip_cooldown" ? "info"
                                          : "warn"}>
                              {t.action}
                            </Badge>
                          </td>
                          <td className="py-1 px-2 text-right font-mono">
                            {t.current_price ? `$${Number(t.current_price).toFixed(4)}` : "—"}
                          </td>
                          <td className="py-1 px-2 text-right font-mono">
                            {t.seven_day_low ? `$${Number(t.seven_day_low).toFixed(4)}` : "—"}
                          </td>
                          <td className="py-1 px-2 text-muted truncate max-w-[24rem]">
                            {(t.reason || "").slice(0, 110)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
